import { useEffect, useCallback } from 'react'
import { useSyncStore } from '../stores/useSyncStore'
import { useAppStore } from '../stores/useAppStore'
import { workOrderOfflineStorage } from '../services/offlineStorage'
import { offlineJsaService } from '../services/offlineJsaService'
import { offlineWorkOrderService } from '../services/offlineWorkOrderService'

interface SyncSummary {
  workOrderUpdates: {
    pending: number
    failed: number
    details?: Array<{
      id: string
      workOrderNumber?: string
      type: string
      action: string
      timestamp: number
      status: string
      error?: string
    }>
  }
  files: {
    pending: number
    failed: number
    details?: Array<{
      id?: number
      name: string
      workOrderNumber?: string
      size: number
      status: string
      error?: string
    }>
  }
  jsaForms: {
    pending: number
    failed: number
    details?: Array<{
      id: string
      type: string
      technicianName?: string
      workOrderNumber?: string
      date: string
      status: string
      error?: string
    }>
  }
  totalPending: number
  totalFailed: number
}

export const useSyncStatus = () => {
  const { 
    updatePendingCounts, 
    updateFailedCounts,
    setIsSyncing, 
    setSyncProgress, 
    setLastSyncTime,
    setLastSyncError,
    resetSyncState,
    isSyncing 
  } = useSyncStore()
  
  const { isOnline, addNotification } = useAppStore()

  // Fetch current sync status
  const fetchSyncStatus = useCallback(async () => {
    try {
      // Get counts from all services
      const [workOrderCounts, jsaCounts] = await Promise.all([
        workOrderOfflineStorage.getPendingCounts(),
        offlineJsaService.getPendingJsaCounts()
      ])

      // Update store with latest counts
      updatePendingCounts({
        workOrderUpdates: workOrderCounts.workOrderUpdates.pending,
        files: workOrderCounts.files.pending,
        jsaForms: jsaCounts.pending
      })

      updateFailedCounts({
        workOrderUpdates: workOrderCounts.workOrderUpdates.failed,
        files: workOrderCounts.files.failed,
        jsaForms: jsaCounts.failed
      })
    } catch (error) {
      console.error('Error fetching sync status:', error)
    }
  }, [updatePendingCounts, updateFailedCounts])

  // Get detailed sync summary
  const getSyncSummary = useCallback(async (includeDetails = false): Promise<SyncSummary> => {
    const [workOrderDetails, fileDetails, jsaDetails] = await Promise.all([
      includeDetails ? workOrderOfflineStorage.getDetailedPendingUpdates() : null,
      includeDetails ? workOrderOfflineStorage.getDetailedPendingFiles() : null,
      includeDetails ? offlineJsaService.getDetailedPendingJsas() : null
    ])

    const [workOrderCounts, jsaCounts] = await Promise.all([
      workOrderOfflineStorage.getPendingCounts(),
      offlineJsaService.getPendingJsaCounts()
    ])

    return {
      workOrderUpdates: {
        pending: workOrderCounts.workOrderUpdates.pending,
        failed: workOrderCounts.workOrderUpdates.failed,
        details: workOrderDetails?.workOrderUpdates.map(u => ({
          id: u.id,
          workOrderNumber: u.workOrderNumber,
          type: u.type,
          action: u.action,
          timestamp: u.timestamp,
          status: u.status,
          error: u.error
        }))
      },
      files: {
        pending: workOrderCounts.files.pending,
        failed: workOrderCounts.files.failed,
        details: fileDetails?.files.map(f => ({
          id: f.id,
          name: f.name,
          workOrderNumber: f.workOrderNumber,
          size: f.file.size,
          status: f.status,
          error: f.error
        }))
      },
      jsaForms: {
        pending: jsaCounts.pending,
        failed: jsaCounts.failed,
        details: jsaDetails?.jsaForms
      },
      totalPending: workOrderCounts.total + jsaCounts.total,
      totalFailed: workOrderCounts.workOrderUpdates.failed + 
                   workOrderCounts.files.failed + 
                   jsaCounts.failed
    }
  }, [])

  // Sync all pending items
  const syncAll = useCallback(async () => {
    if (!isOnline || isSyncing) return

    try {
      setIsSyncing(true)
      setSyncProgress(0)
      
      // Count total items to sync
      const summary = await getSyncSummary()
      const totalItems = summary.totalPending
      let syncedItems = 0

      await offlineWorkOrderService.syncAllPendingChanges()
      syncedItems += summary.workOrderUpdates.pending + summary.files.pending
      setSyncProgress(Math.round((syncedItems / totalItems) * 100))

      // Sync JSA forms
      if (summary.jsaForms.pending > 0) {
        await offlineJsaService.syncAllPendingJsas()
        syncedItems += summary.jsaForms.pending
        setSyncProgress(100)
      }

      setLastSyncTime(new Date())
      addNotification({
        type: 'success',
        message: `Successfully synced ${totalItems} items`,
        timestamp: Date.now()
      })
    } catch (error) {
      setLastSyncError(error instanceof Error ? error.message : 'Sync failed')
      addNotification({
        type: 'error',
        message: 'Some items failed to sync. Check sync details.',
        timestamp: Date.now()
      })
    } finally {
      resetSyncState()
      await fetchSyncStatus()
    }
  }, [isOnline, isSyncing, getSyncSummary, setIsSyncing, setSyncProgress, 
      setLastSyncTime, setLastSyncError, resetSyncState, fetchSyncStatus, 
      addNotification])

  // Sync individual item
  const syncItem = useCallback(async (id: string | number, type: 'workorder' | 'file' | 'jsa') => {
    if (!isOnline) return

    try {
      switch (type) {
        case 'workorder':
          await workOrderOfflineStorage.retryFailedUpdate(id as string)
          await offlineWorkOrderService.syncAllPendingChanges()
          break
        case 'file':
          await workOrderOfflineStorage.retryFailedFile(id as number)
          await offlineWorkOrderService.syncAllPendingChanges()
          break
        case 'jsa':
          offlineJsaService.retryFailedJsa(id as string)
          await offlineJsaService.syncAllPendingJsas()
          break
      }
      
      addNotification({
        type: 'success',
        message: 'Item synced successfully',
        timestamp: Date.now()
      })
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to sync item',
        timestamp: Date.now()
      })
    } finally {
      await fetchSyncStatus()
    }
  }, [isOnline, addNotification, fetchSyncStatus])

  // Clear failed items
  const clearFailed = useCallback(async () => {
    try {
      await Promise.all([
        workOrderOfflineStorage.clearFailedItems(),
        offlineJsaService.clearFailedJsas()
      ])
      
      await fetchSyncStatus()
      
      addNotification({
        type: 'info',
        message: 'Failed items cleared',
        timestamp: Date.now()
      })
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'Failed to clear items',
        timestamp: Date.now()
      })
    }
  }, [fetchSyncStatus, addNotification])

  // Auto-refresh sync status
  useEffect(() => {
    fetchSyncStatus()
    
    // Refresh every 30 seconds
    const interval = setInterval(fetchSyncStatus, 30000)
    
    return () => clearInterval(interval)
  }, [fetchSyncStatus])

  return {
    fetchSyncStatus,
    getSyncSummary,
    syncAll,
    syncItem,
    clearFailed
  }
}